import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' };
const muted = { color: '#64748b', fontSize: '12px' };

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [examples, setExamples] = useState(null);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ suchbegriff: '', kategorie: '', aktiv: true });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setError('');
    try {
      const [r, s, a] = await Promise.all([api.get('/regeln'), api.get('/regeln/stats'), api.get('/rules/applications')]);
      setRules(r.data);
      setStats(s.data.stats);
      setCategories(s.data.kategorien || []);
      setApplications(a.data || []);
      if (!form.kategorie && s.data.kategorien?.[0]) setForm(f => ({ ...f, kategorie: s.data.kategorien[0].name }));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Regeln konnten nicht geladen werden');
    }
  }

  const filtered = useMemo(() => {
    const f = filter.toUpperCase();
    return rules.filter(r =>
      (!f || `${r.suchbegriff} ${r.kategorie}`.toUpperCase().includes(f)) &&
      (!categoryFilter || r.kategorie === categoryFilter)
    );
  }, [rules, filter, categoryFilter]);

  async function saveRule(e) {
    e.preventDefault();
    setBusy('save');
    setError('');
    setMessage('');
    try {
      if (form.id) await api.put(`/regeln/${form.id}`, form);
      else await api.post('/regeln', form);
      setMessage(form.id ? 'Regel aktualisiert.' : 'Regel erstellt.');
      setForm({ suchbegriff: '', kategorie: categories[0]?.name || '', aktiv: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Speichern fehlgeschlagen');
    } finally {
      setBusy('');
    }
  }

  async function edit(rule) {
    setForm({ id: rule.id, suchbegriff: rule.suchbegriff, kategorie: rule.kategorie, aktiv: rule.aktiv !== 0 });
  }

  async function toggle(rule) {
    await api.put(`/regeln/${rule.id}`, { ...rule, aktiv: rule.aktiv === 0 });
    await load();
  }

  async function remove(rule) {
    if (!window.confirm(`Regel "${rule.suchbegriff}" wirklich löschen?`)) return;
    await api.delete(`/regeln/${rule.id}`);
    if (selected?.id === rule.id) { setSelected(null); setExamples(null); }
    await load();
  }

  async function select(rule) {
    setSelected(rule);
    setExamples(null);
    try {
      const r = await api.get(`/regeln/${rule.id}/beispiele`);
      setExamples(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Beispiele konnten nicht geladen werden');
    }
  }

  async function applyRule(rule) {
    if (!window.confirm(`Regel "${rule.suchbegriff}" auf passende Buchungen anwenden?`)) return;
    const r = await api.post(`/regeln/${rule.id}/anwenden`);
    const undoText = r.data.applicationId ? ` Undo-ID ${r.data.applicationId}.` : '';
    setMessage(`${r.data.geaendert} Buchungen aktualisiert.${undoText}`);
    await load();
    await select(rule);
  }

  async function runTest(e) {
    e.preventDefault();
    if (!testText.trim()) return;
    const r = await api.get('/regeln/test', { params: { text: testText } });
    setTestResult(r.data);
  }

  async function undoApplication(appRow) {
    if (!window.confirm(`Regelanwendung ${appRow.id} wirklich rückgängig machen?`)) return;
    setBusy(`undo-${appRow.id}`);
    setError('');
    setMessage('');
    try {
      const r = await api.post(`/rules/applications/${appRow.id}/undo`);
      setMessage(`${r.data.restored} Buchungen wiederhergestellt.`);
      await load();
      if (selected) await select(selected);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Rückgängig machen fehlgeschlagen');
    } finally {
      setBusy('');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: '23px' }}>Regel- und KI-Kontrolle</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Regeln prüfen, testen, bearbeiten und kontrolliert auf Buchungen anwenden.</p>
        </div>
        <button onClick={load} style={button('#0f172a')}>Neu laden</button>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <Stat title="Regeln" value={stats?.gesamt ?? rules.length} />
        <Stat title="Aktiv" value={stats?.aktiv ?? rules.filter(r => r.aktiv !== 0).length} />
        <Stat title="Inaktiv" value={stats?.inaktiv ?? rules.filter(r => r.aktiv === 0).length} />
        <Stat title="Kategorien" value={stats?.kategorien ?? categories.length} />
      </div>

      <section style={{ ...card, marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
          <div>
            <h2 style={h2}>Letzte Regelanwendungen</h2>
            <p style={{ ...muted, margin: 0 }}>Jede Anwendung merkt sich die vorherigen Kategorien und kann hier gezielt zurückgenommen werden.</p>
          </div>
          <button onClick={load} style={smallButton('#0f172a')}>Aktualisieren</button>
        </div>
        {applications.length === 0 && <div style={muted}>Noch keine Regelanwendungen mit Undo-Protokoll vorhanden.</div>}
        {applications.length > 0 && (
          <div style={{ display: 'grid', gap: '7px' }}>
            {applications.slice(0, 8).map(appRow => (
              <div key={appRow.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                <div>
                  <strong style={{ color: '#0f172a', fontSize: '13px' }}>{appRow.suchbegriff} -> {appRow.kategorie}</strong>
                  <div style={muted}>{appRow.affected_count} Buchungen · {appRow.created_at} · {appRow.undone_at ? `zurückgesetzt ${appRow.undone_at}` : 'aktiv'}</div>
                </div>
                {!appRow.undone_at ? (
                  <button disabled={busy === `undo-${appRow.id}`} onClick={() => undoApplication(appRow)} style={smallButton('#dc2626')}>Rückgängig</button>
                ) : (
                  <span style={{ ...muted, fontWeight: 900 }}>Erledigt</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <section style={card}>
          <h2 style={h2}>Regel speichern</h2>
          <form onSubmit={saveRule} style={{ display: 'grid', gap: '8px' }}>
            <input value={form.suchbegriff} onChange={e => setForm({ ...form, suchbegriff: e.target.value })} placeholder="Suchbegriff, z.B. NETFLIX" style={input} />
            <select value={form.kategorie} onChange={e => setForm({ ...form, kategorie: e.target.value })} style={input}>
              {categories.map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
            </select>
            <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={form.aktiv} onChange={e => setForm({ ...form, aktiv: e.target.checked })} />
              Regel aktiv
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={busy === 'save'} style={button('#2563eb')}>{form.id ? 'Aktualisieren' : 'Erstellen'}</button>
              {form.id && <button type="button" onClick={() => setForm({ suchbegriff: '', kategorie: categories[0]?.name || '', aktiv: true })} style={button('#64748b')}>Abbrechen</button>}
            </div>
          </form>
        </section>

        <section style={card}>
          <h2 style={h2}>Regeltest</h2>
          <form onSubmit={runTest} style={{ display: 'grid', gap: '8px' }}>
            <input value={testText} onChange={e => setTestText(e.target.value)} placeholder="Empfänger oder Verwendungszweck testen" style={input} />
            <button style={button('#16a34a')}>Testen</button>
          </form>
          {testResult && (
            <div style={{ marginTop: '10px', background: '#f8fafc', borderRadius: '9px', padding: '10px' }}>
              <strong style={{ color: '#0f172a' }}>Kategorie: {testResult.kategorie}</strong>
              <div style={muted}>Quelle: {testResult.quelle} ?? Treffer: {testResult.matches.length}</div>
              {testResult.matches.slice(0, 4).map(m => <div key={m.id} style={{ fontSize: '12px', marginTop: '5px' }}>{m.suchbegriff} -> {m.kategorie}</div>)}
            </div>
          )}
        </section>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(0, 1fr)', gap: '12px' }}>
        <section style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '8px', marginBottom: '10px' }}>
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Regeln suchen..." style={input} />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={input}>
              <option value="">Alle Kategorien</option>
              {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ maxHeight: '620px', overflow: 'auto' }}>
            {filtered.map(rule => (
              <div key={rule.id} style={{ borderTop: '1px solid #f1f5f9', padding: '9px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <button onClick={() => select(rule)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', flex: 1 }}>
                    <strong style={{ color: rule.aktiv === 0 ? '#94a3b8' : '#0f172a', fontSize: '13px' }}>{rule.suchbegriff}</strong>
                    <div style={muted}>{rule.kategorie} ?? {rule.aktiv === 0 ? 'inaktiv' : 'aktiv'}</div>
                  </button>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <button onClick={() => edit(rule)} style={smallButton('#2563eb')}>Edit</button>
                    <button onClick={() => toggle(rule)} style={smallButton(rule.aktiv === 0 ? '#16a34a' : '#f59e0b')}>{rule.aktiv === 0 ? 'An' : 'Aus'}</button>
                    <button onClick={() => remove(rule)} style={smallButton('#dc2626')}>Del</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={card}>
          <h2 style={h2}>Regel-Details</h2>
          {!selected && <div style={muted}>Wähle links eine Regel aus.</div>}
          {selected && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                  <strong style={{ color: '#0f172a', fontSize: '16px' }}>{selected.suchbegriff}</strong>
                  <div style={muted}>{selected.kategorie} ?? {selected.aktiv === 0 ? 'inaktiv' : 'aktiv'}</div>
                </div>
                <button onClick={() => applyRule(selected)} style={button('#7c3aed')}>Auf Buchungen anwenden</button>
              </div>
              <h3 style={{ margin: '12px 0 8px', color: '#0f172a', fontSize: '14px' }}>Passende Buchungen</h3>
              {!examples && <div style={muted}>Lädt...</div>}
              {examples?.buchungen?.length === 0 && <div style={muted}>Keine passenden Buchungen gefunden.</div>}
              <div style={{ maxHeight: '520px', overflow: 'auto' }}>
                {examples?.buchungen?.map(b => (
                  <div key={b.id} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <strong style={{ color: '#0f172a', fontSize: '13px' }}>{b.empfaenger || b.verwendungszweck}</strong>
                      <span style={{ color: b.betrag >= 0 ? '#16a34a' : '#dc2626', fontWeight: 900 }}>{Number(b.betrag).toFixed(2)} EUR</span>
                    </div>
                    <div style={muted}>{b.datum} ?? aktuell: {b.kategorie || 'ohne Kategorie'} ?? KI: {b.ki_kategorie || '-'}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const h2 = { margin: '0 0 10px', color: '#0f172a', fontSize: '16px' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '9px', border: '1px solid #cbd5e1', background: 'white' };

function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '9px', padding: '9px 11px', cursor: 'pointer', fontWeight: 900, fontSize: '12px' };
}

function smallButton(bg) {
  return { ...button(bg), padding: '6px 8px', fontSize: '11px' };
}

function Stat({ title, value }) {
  return <div style={card}><div style={{ ...muted, fontWeight: 800, textTransform: 'uppercase' }}>{title}</div><div style={{ color: '#0f172a', fontWeight: 950, fontSize: '22px', marginTop: '5px' }}>{value}</div></div>;
}

function Notice({ tone, children }) {
  const map = tone === 'red' ? ['#fee2e2', '#991b1b', '#fecaca'] : ['#dcfce7', '#166534', '#86efac'];
  return <div style={{ background: map[0], color: map[1], border: `1px solid ${map[2]}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{children}</div>;
}
